import React, { useEffect, useRef, useState } from 'react';

import { extractErrorMessage } from '../../api/errors';
import { messagingApi } from '../../api/messaging';
import Button from '../ui/Button';

const POLL_MS = 5000;

function formatCurrency(n) {
  return `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
}

function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

export default function OrderChat({ orderId, currentUserId, allowCounterOffer = false }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [body, setBody] = useState('');
  const [offerOpen, setOfferOpen] = useState(false);
  const [offer, setOffer] = useState({ proposed_price: '', proposed_quantity: '' });
  const [submitting, setSubmitting] = useState(false);
  const listRef = useRef(null);

  const fetchMessages = async () => {
    try {
      const data = await messagingApi.listMessages(orderId);
      setMessages(data);
      setError('');
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Initial load + polling
  useEffect(() => {
    fetchMessages();
    const id = setInterval(fetchMessages, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // Auto-scroll to bottom when message count grows
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages.length]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    const payload = { body: body.trim() };
    if (offerOpen) {
      if (!offer.proposed_price || !offer.proposed_quantity) {
        setError('Counter-offer needs both price and quantity.');
        return;
      }
      payload.proposed_price = offer.proposed_price;
      payload.proposed_quantity = offer.proposed_quantity;
    }
    if (!payload.body && !offerOpen) return;
    setSubmitting(true);
    setError('');
    try {
      const created = await messagingApi.sendMessage(orderId, payload);
      setMessages((prev) => [...prev, created]);
      setBody('');
      setOfferOpen(false);
      setOffer({ proposed_price: '', proposed_quantity: '' });
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div
        ref={listRef}
        className="max-h-72 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3"
      >
        {loading ? (
          <p className="text-xs text-gray-500">Loading messages…</p>
        ) : messages.length === 0 ? (
          <p className="text-xs text-gray-500">
            No messages yet. Start the conversation — the {allowCounterOffer ? 'buyer' : 'FPO admin'} will get a notification.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {messages.map((m) => {
              const mine = m.sender === currentUserId;
              const isOffer = m.proposed_price !== null && m.proposed_quantity !== null;
              return (
                <li
                  key={m.id}
                  className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-xl px-3 py-2 text-sm shadow-sm ${
                      mine
                        ? 'bg-green-700 text-white'
                        : 'bg-white text-gray-900 border border-gray-200'
                    }`}
                  >
                    <div className={`mb-0.5 text-[10px] uppercase tracking-wider ${mine ? 'text-green-100' : 'text-gray-500'}`}>
                      {m.sender_full_name || m.sender_username} · {m.sender_role.replace('_', ' ')} · {formatTime(m.created_at)}
                    </div>
                    {isOffer && (
                      <div className={`mb-1 rounded-md px-2 py-1 text-xs font-semibold ${mine ? 'bg-green-800/40' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
                        💬 Counter-offer: {formatCurrency(m.proposed_price)} × {Number(m.proposed_quantity).toFixed(2)}
                      </div>
                    )}
                    {m.body && <div className="whitespace-pre-line">{m.body}</div>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      <form onSubmit={submit} className="flex flex-col gap-2">
        <div className="flex items-start gap-2">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={2}
            placeholder="Type a message…"
            className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
          />
          <Button type="submit" size="sm" disabled={submitting}>
            {submitting ? 'Sending…' : 'Send'}
          </Button>
        </div>

        {allowCounterOffer && (
          <div>
            <button
              type="button"
              onClick={() => setOfferOpen((v) => !v)}
              className="text-xs font-semibold text-green-700 hover:text-green-800"
            >
              {offerOpen ? 'Cancel counter-offer' : '+ Add a counter-offer'}
            </button>
            {offerOpen && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Proposed price"
                  value={offer.proposed_price}
                  onChange={(e) => setOffer({ ...offer, proposed_price: e.target.value })}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
                />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Proposed quantity"
                  value={offer.proposed_quantity}
                  onChange={(e) => setOffer({ ...offer, proposed_quantity: e.target.value })}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
                />
              </div>
            )}
          </div>
        )}
      </form>
    </div>
  );
}
